import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';

interface ProgramCardProps {
  title: string;
  description: string;
  schedule: string;
  image?: string;
}

const DAY_ORDER: Record<string, number> = {
  'mon': 1, 'monday': 1, 'tue': 2, 'tuesday': 2, 'wed': 3, 'wednesday': 3,
  'thu': 4, 'thursday': 4, 'fri': 5, 'friday': 5, 'sat': 6, 'saturday': 6,
  'sun': 7, 'sunday': 7,
};

const DAY_ABBREV: Record<string, string> = {
  'mon': 'Mon', 'monday': 'Mon', 'tue': 'Tue', 'tuesday': 'Tue', 'wed': 'Wed', 'wednesday': 'Wed',
  'thu': 'Thu', 'thursday': 'Thu', 'fri': 'Fri', 'friday': 'Fri', 'sat': 'Sat', 'saturday': 'Sat',
  'sun': 'Sun', 'sunday': 'Sun',
};

function parseDays(schedule: string): string[] {
  const daysSet = new Set<string>();
  const parts = schedule.split('|');
  parts.forEach(part => {
    const dayMatch = part.match(/^(.*?):/);
    const dayPart = dayMatch ? dayMatch[1].trim() : part.trim();
    const dayStrings = dayPart.split(/[&\s,/]+/).map(d => d.trim()).filter(d => d.length > 0);
    dayStrings.forEach(day => {
      const dayLower = day.toLowerCase();
      if (DAY_ORDER[dayLower]) daysSet.add(dayLower);
    });
  });
  return Array.from(daysSet).sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]).map(day => DAY_ABBREV[day]);
}

export default function ProgramCard({ title, description, schedule, image }: ProgramCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const days = useMemo(() => parseDays(schedule), [schedule]);

  return (
    <motion.div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -10 }}
      className="group relative bg-white overflow-hidden rounded-sm transition-all duration-500 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.15)] h-full flex flex-col"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={image || '/images/hero-team.jpg'}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale group-hover:grayscale-0"
        />
        <div className="absolute inset-0 bg-brand-black/20 group-hover:bg-transparent transition-colors duration-500" />
        
        <div className="absolute top-6 left-6">
          <span className="px-4 py-2 bg-brand-black text-white text-[10px] font-bold tracking-[0.2em] uppercase rounded-sm">
            Curriculum
          </span>
        </div>
      </div>

      <div className="p-8 md:p-12 border-x border-b border-gray-100 flex flex-col flex-grow">
        <h3 className="text-3xl font-display font-bold text-brand-black mb-6 tracking-tighter group-hover:text-brand-blue transition-colors">
          {title}
        </h3>
        
        <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8 flex-grow">
          {description}
        </p>

        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {days.map((day, i) => (
              <span key={i} className="text-[10px] font-bold uppercase tracking-widest text-brand-blue bg-brand-blue/10 px-3 py-1 rounded-full">
                {day}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-50">
            <Link 
              to="/schedule" 
              className="flex items-center gap-2 text-gray-400 hover:text-brand-blue transition-colors group"
            >
              <Clock className="w-5 h-5 group-hover:text-brand-blue transition-colors" />
              <span className="text-sm md:text-base font-bold uppercase tracking-widest">Visit Class Schedules</span>
            </Link>
            
            <Link 
              to="/brazilian-jiu-jitsu-training-programs" 
              className={`w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center transition-all hover:bg-brand-blue hover:border-brand-blue ${isHovered ? 'bg-brand-blue border-brand-blue' : ''}`}
            >
              <ArrowRight className={`w-5 h-5 transition-colors ${isHovered ? 'text-white' : 'text-gray-400'}`} />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
